// Clients (Workspace 2, screen 2): the firm's clients you can see, and one client's page — its engagements (cards as on
// Home) and its Action center rolled up across them.
import { Link, useParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "../../components";
import { fmtDate } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { groupBy, typeLabel } from "../../lib/workspace";
import { ActionCenter } from "./ActionCenter";
import { useManyWs } from "./common";
import { EngagementCard } from "./Home";

export function ClientsPage() {
  const { ds } = useApp();
  const es = useLoad(() => ds.engagements(), [ds]);
  const ws = useManyWs((es.data ?? []).map((e) => e.id));
  const clients = groupBy(es.data ?? [], (e) => e.clientId).sort(([, a], [, b]) => a[0].clientName.localeCompare(b[0].clientName));
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <PageHeader title="Clients" subtitle="Every client with an engagement you can see." />
      {es.error ? <ErrorState error={es.error} onRetry={es.reload} /> : !es.data ? <LoadingRows rows={4} label="Loading clients" /> : !clients.length ? (
        <EmptyState title="No clients you can see yet." />
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised">
          {clients.map(([cid, engs]) => {
            const open = engs.reduce((n, e) => n + (ws.data?.get(e.id)?.attention.length ?? 0), 0);
            return (
              <li key={cid}>
                <Link to={`/clients/${encodeURIComponent(cid)}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-sunken">
                  <span className="min-w-0 flex-1"><span className="block font-medium">{engs[0].clientName}</span>
                    <span className="block text-xs text-muted">{engs.map((e) => `${typeLabel(e.engagementType)} FYE ${fmtDate(e.fye)}`).join(" · ")}</span></span>
                  <span className="text-xs text-muted">{engs.length} engagement{engs.length === 1 ? "" : "s"} · {open} open item{open === 1 ? "" : "s"}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ClientPage() {
  const { clientId = "" } = useParams();
  const cid = decodeURIComponent(clientId);
  const { ds } = useApp();
  const es = useLoad(() => ds.engagements(), [ds]);
  const mine = (es.data ?? []).filter((e) => e.clientId === cid).sort((a, b) => b.fye.localeCompare(a.fye));
  const ws = useManyWs(mine.map((e) => e.id));
  if (es.error) return <ErrorState error={es.error} onRetry={es.reload} />;
  if (!es.data) return <LoadingRows rows={4} label="Loading the client" />;
  if (!mine.length) return <EmptyState title="No engagements for this client that you can see."><Link className="text-brand underline" to="/clients">All clients</Link></EmptyState>;
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader crumbs={<Link to="/clients" className="hover:underline">Clients</Link>} title={mine[0].clientName}
        subtitle={`${mine.length} engagement${mine.length === 1 ? "" : "s"}${mine[0].partner ? ` · partner ${mine[0].partner}` : ""}`} />
      <section aria-labelledby="client-engs" className="space-y-2">
        <h2 id="client-engs" className="text-sm font-semibold">Engagements</h2>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {mine.map((e) => <li key={e.id}><EngagementCard e={e} attention={ws.data?.get(e.id)?.attention ?? []} docs={ws.data?.get(e.id)?.pbc ?? []} /></li>)}
        </ul>
      </section>
      <ActionCenter scope={{ kind: "client", clientId: cid }} embedded />
    </div>
  );
}
